import { useCallback, useMemo } from "react";
import { togalLogin } from "../../integrations/qto/sources/togal/api";
import { exchangeAuthorizationCode } from "../../integrations/qto/sources/ato/auth";
import { handleError } from "../../errors";
import Logger from "../../clients/sentry";

/**
 * Take-off integration sign-in. "Log in" is a top-level action, so this hook owns Logger.error for
 * both sources (docs/error-handling-and-logging.md); UI copy stays with the login component.
 *
 * The returned actions are memoized because the login component subscribes to the OAuth `message`
 * event with them in its dependency list.
 */
export function useTakeOffLoginActions() {
  const handleTogalLogin = useCallback(async (email: string, password: string): Promise<boolean> => {
    const result = await togalLogin(email, password);
    if (result.success) return true;

    Logger.error("Togal login failed", handleError(result.error, { context: "togalLogin" }));
    return false;
  }, []);

  const handleAtoTokenExchange = useCallback(async (code: string): Promise<void> => {
    try {
      await exchangeAuthorizationCode(code);
    } catch (error) {
      const err = handleError(error, { context: "exchangeAuthorizationCode" });
      Logger.error("Autodesk token exchange failed", err);
      throw err;
    }
  }, []);

  const logAtoDialogFailure = useCallback((reason: string, code?: number): void => {
    const err = handleError(new Error(reason), { context: "atoSignInDialog", code });
    Logger.error("Autodesk sign-in dialog failed", err, { code });
  }, []);

  return useMemo(
    () => ({ handleTogalLogin, handleAtoTokenExchange, logAtoDialogFailure }),
    [handleTogalLogin, handleAtoTokenExchange, logAtoDialogFailure]
  );
}

export default useTakeOffLoginActions;
